import { create } from 'zustand';
import type { ContextMenuNode, ContextMenuPayload, ContextMenuWidth } from './types';

/**
 * Единственный открытый экземпляр ПКМ-меню на всё приложение (SOLUTION §6).
 *
 * Store, а не React-context: второй ПКМ обязан закрыть первый независимо от
 * того, на какой поверхности он открыт, — общего родителя у строки таблицы,
 * бара ганта и заголовка workspace нет.
 */

type State = {
  payload: ContextMenuPayload | null;
};

type Actions = {
  open: (payload: ContextMenuPayload) => void;
  close: () => void;
};

export const useContextMenuStore = create<State & Actions>((set) => ({
  payload: null,
  open(payload) {
    set({ payload });
  },
  close() {
    set({ payload: null });
  },
}));

/**
 * Открыть меню из обычного колбэка — там, где хук неприменим: строки таблицы
 * рендерятся в цикле, и `useContextMenu` на строку вызвать нельзя.
 * Возвращает false, если поверхность отказалась от меню (браузерное остаётся).
 */
export function openContextMenuFromEvent(
  e: {
    clientX: number;
    clientY: number;
    currentTarget: EventTarget | null;
    target: EventTarget | null;
    preventDefault: () => void;
    stopPropagation?: () => void;
  },
  args: OpenContextMenuArgs | null,
): boolean {
  if (!args || args.nodes.length === 0) return false;
  if (shouldKeepNativeMenu(e.target)) return false;
  e.preventDefault();
  // Внутренний носитель выигрывает у внешнего: без этого меню пустого места
  // (F13), висящее на фоне вида, перехватывало КАЖДЫЙ ПКМ по строке — строка
  // открывала своё меню, а всплывший тот же клик тут же подменял его своим.
  e.stopPropagation?.();
  useContextMenuStore.getState().open({
    x: e.clientX,
    y: e.clientY,
    source: e.currentTarget instanceof HTMLElement ? e.currentTarget : null,
    ...args,
  });
  return true;
}

/**
 * F3 (project-actions-parity §4.2) — открыть меню ОТ КНОПКИ, а не от события мыши.
 *
 * `openContextMenuFromEvent` берёт `clientX/clientY` события. Для ПКМ это верно, для
 * видимой кнопки-носителя — работает только при клике мышью: активация с клавиатуры
 * (`Enter`/`Space`) даёт нули, и меню открывается в левом верхнем углу экрана. Пока
 * единственным входом был ПКМ, дефект был недостижим; с появлением кебабов (F5, F11,
 * F4) он становится обычным путём.
 *
 * Точка считается от прямоугольника кнопки — нижний левый угол, как у выпадающих
 * меню везде. Модель узлов примитива не меняется: `Placement` уже умеет якорь,
 * менять его ради этого не нужно — нужен только другой вход (SOLUTION §8.2-О7).
 * Collision-flip у нижней кромки вьюпорта делает примитив сам.
 */
export function openContextMenuFromButton(
  el: HTMLElement | null,
  args: OpenContextMenuArgs | null,
): boolean {
  if (!args || args.nodes.length === 0 || !el) return false;
  const rect = el.getBoundingClientRect();
  useContextMenuStore.getState().open({
    x: rect.left,
    y: rect.bottom + 4,
    // `source` — куда вернуть фокус по `Esc`. Примитив это уже умеет; без ссылки
    // на кнопку фокус после закрытия уезжал бы в начало документа.
    source: el,
    ...args,
  });
  return true;
}

export type OpenContextMenuArgs = {
  nodes: ContextMenuNode[];
  width: ContextMenuWidth;
  ariaLabel: string;
};

/**
 * Поля ввода и редакторы отдаём браузеру: вставка и спеллчек важнее нашего
 * меню (SOLUTION §6, инвариант I7). Выделенный текст — тоже: пользователь
 * целится в «Копировать» браузера.
 */
export function shouldKeepNativeMenu(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  if (target.closest('input, textarea, select, [contenteditable="true"], .ProseMirror'))
    return true;
  const selection = typeof window !== 'undefined' ? window.getSelection() : null;
  if (selection && !selection.isCollapsed && selection.toString().trim().length > 0) {
    // Выделение считается «своим», только если оно внутри цели ПКМ.
    const anchor = selection.anchorNode;
    if (anchor && target.contains(anchor)) return true;
  }
  return false;
}
